import React from 'react';
// @mui
import { Close } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
// types
import { baseQueryKey, VoicemailSettings } from 'src/hooks/core-api/useVoicemailSettings';
import { formSchema } from './schema';
// hooks
import { useSnackbar } from 'notistack';
import useUpdateVoicemailSettings, {
  UpdateVoicemailSettingsRequest,
} from 'src/hooks/core-api/useUpdateVoicemailSettings';
//
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import * as z from 'zod';

interface FormProps {
  data: VoicemailSettings;
}

type FormSchema = z.infer<typeof formSchema>;

const propDataToFormData = (data: VoicemailSettings): FormSchema => ({
  send_notifications: data.send_notifications,
  // useFieldArray React Hook Form fields must be an array of objects
  voicemail_email_addresses: data.voicemail_email_addresses.map((email) => ({
    email: email,
  })),
  alv_id: data.alv_id,
});

const formDataToRequestData = (data: FormSchema): UpdateVoicemailSettingsRequest => ({
  send_notifications: data.send_notifications,
  // API expects an array of strings
  voicemail_email_addresses: data.voicemail_email_addresses.map((email) => email.email),
  alv_id: data.alv_id,
});

const Form: React.FC<FormProps> = ({ data }) => {
  const { mutate, isLoading } = useUpdateVoicemailSettings();

  const formDefaults = propDataToFormData(data);

  const methods = useForm({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: formDefaults,
  });

  const { control } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'voicemail_email_addresses',
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const onSubmit = (data: FormSchema) => {
    mutate(formDataToRequestData(data), {
      onSuccess: () => {
        queryClient.invalidateQueries([baseQueryKey, '*']);
        enqueueSnackbar('Voicemail settings updated', { variant: 'success' });
      },
      onError: (error) => {
        console.error(error);
        enqueueSnackbar(`Error updating voicemail settings`, { variant: 'error' });
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Box my={4}>
          <Controller
            name="send_notifications"
            control={control}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <FormControlLabel
                control={<Switch checked={value} onChange={onChange} size="medium" />}
                label="Send email notifications to the following email addresses"
              />
            )}
          />
        </Box>
        {fields.map((item, index) => (
          <Tooltip
            key={item.id}
            placement="top"
            title={
              index === fields.length - 1
                ? 'Add the email address that you would prefer voicemail notifications to be sent to'
                : ''
            }
          >
            <Box mt={3}>
              <Controller
                name={`voicemail_email_addresses.${index}.email`}
                control={control}
                render={({ field: { ref, ...otherField }, fieldState: { error } }) => (
                  <TextField
                    InputProps={{
                      autoComplete: 'none',
                      startAdornment:
                        fields.length > 1 ? (
                          <Tooltip title="Remove email">
                            <IconButton onClick={() => remove(index)}>
                              <Close />
                            </IconButton>
                          </Tooltip>
                        ) : null,
                    }}
                    inputRef={ref}
                    helperText={error ? error.message : null}
                    error={!!error}
                    label={index === fields.length - 1 ? 'Add email' : ''}
                    fullWidth
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && otherField.value && !error) {
                        e.preventDefault();
                        append({ email: '' });
                      }
                      if (e.key === 'Backspace' && !otherField.value) {
                        e.preventDefault();
                        remove(index);
                      }
                    }}
                    {...otherField}
                  />
                )}
              />
            </Box>
          </Tooltip>
        ))}

        <Box mt={1}>
          <Button
            variant="text"
            size="large"
            sx={{ color: 'text.secondary' }}
            onClick={() => append({ email: '' })}
          >
            + Additional Email
          </Button>
        </Box>

        <Box mt={3}>
          <LoadingButton
            variant="contained"
            size="large"
            type="submit"
            disabled={!methods.formState.isValid}
            loading={isLoading}
          >
            Save
          </LoadingButton>
        </Box>
      </form>
    </FormProvider>
  );
};

export default Form;
